import type { ApplicationContext } from '@hc-frontend/core-context';
import type {
  DomainSessionId,
  DomainUserId,
  HealthcareSessionId,
  HealthcareUserId,
} from '@hc-frontend/core-types';
import type { IErrorOperationResult } from '@hc-frontend/core-utils-business-operation';
import {
  createErrorOperationResult,
  createSuccessOperationResult,
} from '@hc-frontend/core-utils-business-operation';

import type { LeadStatus } from '../../entities';
import { LeadIdentifier, Prospect } from '../../entities';
import type {
  GetProspectRepository,
  GetProspectResult,
} from '../../repositories';

export type GetProspectUseCase = (
  sessionId: HealthcareSessionId,
  userId: HealthcareUserId,
  domainSessionId?: DomainSessionId,
  domainUserId?: DomainUserId,
  leadStatus?: LeadStatus,
) => GetProspectResult;

type GetProspectFactoryParameters = GetProspectRepository &
  Pick<ApplicationContext, 'log'>;

const logOperation = (
  log: ApplicationContext['log'],
  result: IErrorOperationResult<string>,
) => {
  log({
    context: 'server',
    type: 'error',
    message: result.errors[0].code,
    data: {
      rootCause: result.errors[0].rootCause,
    },
  });
};

export function getProspectFactory({
  getProspect,
  log,
}: GetProspectFactoryParameters): GetProspectUseCase {
  return async (
    sessionId,
    userId,
    domainSessionId,
    domainUserId,
    leadStatus,
  ) => {
    const identifier = LeadIdentifier.safeParse({
      sessionId,
      userId,
    });

    if (!identifier.success) {
      const error = createErrorOperationResult({
        errors: [
          {
            code: 'failedToGetProspect',
            rootCause: identifier.error,
          },
        ],
      });

      logOperation(log, error);

      return error;
    }

    const leadStorageResponse = await getProspect({
      sessionId,
      userId,
      domainSessionId,
      domainUserId,
      leadStatus,
    });

    if (!leadStorageResponse.success) {
      logOperation(log, leadStorageResponse);
      return leadStorageResponse;
    }

    const prospectSession = Prospect.safeParse(leadStorageResponse.data);

    if (!prospectSession.success) {
      const error = createErrorOperationResult({
        errors: [
          {
            code: 'failedToGetProspect',
            rootCause: prospectSession.error,
          },
        ],
      });

      logOperation(log, error);

      return error;
    }

    return createSuccessOperationResult(prospectSession.data);
  };
}
